import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Badge,
  Button,
  Card,
  Col,
  Dropdown,
  Input,
  message,
  Modal,
  Pagination,
  Row,
  Select,
  Space,
  Table,
  Tooltip,
  Typography,
} from "antd";
import type { MenuProps } from "antd";
import {
  AppstoreOutlined,
  BankOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExperimentOutlined,
  EyeOutlined,
  FileTextOutlined,
  HistoryOutlined,
  InboxOutlined,
  MoreOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  ShopOutlined,
  StopOutlined,
  SwapOutlined,
  UserOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { useAuth } from "../context/AuthContext";
import SubscriptionStatusBadge from "./components/SubscriptionStatusBadge";
import SubscriptionRenewalEnhancedModal from "./components/SubscriptionRenewalEnhancedModal";
import SubscriptionEditExpiryModal from "./components/SubscriptionEditExpiryModal";
import PlanUpgradeModal from "./components/PlanUpgradeModal";
import SubscriptionDetailsModal from "./components/SubscriptionDetailsModal";
import CompanySubscriptionTermsModal from "./components/CompanySubscriptionTermsModal";
import CompanySubscriptionRestrictionsModal from './components/CompanySubscriptionRestrictionsModal';
import SubscriptionBillingRegularizationModal from "./components/SubscriptionBillingRegularizationModal";
import SubscriberSubscriptionPortal from "./SubscriberSubscriptionPortal";
import AdminPortfolioExportButtons from "../components/AdminPortfolioExportButtons";
import SubscriptionRequestsModal from "./SubscriptionRequestsModal";
import {
  formatSubscriptionDate,
  formatSubscriptionNumber,
} from "./subscriptionFormatters";
import "./GestionSouscription.css";

const { Text, Title } = Typography;
const SEARCH_DELAY_MS = 320;

interface Company {
  id: number;
  name: string;
  short_name: string | null;
  email: string | null;
  phone: string | null;
  status: string;
  trial_ends_at: string | null;
  licence_expire_on: string | null;
  subscription_plan_id: number | null;
  package_type: "monthly" | "quarterly" | "semiannual" | "annual" | null;
  total_users: number;
  active_users: number;
  active_warehouse_count: number;
  created_at: string;
  plan_name: string | null;
  plan_price: number | null;
  plan_currency: string | null;
  plan_billing_cycle: string | null;
  plan_max_users: number | null;
  subscription_status: "active" | "expired" | "trial" | "suspended";
  days_remaining: number | null;
}

interface PaginationInfo {
  current_page: number;
  per_page: number;
  total: number;
  total_pages: number;
}

interface SubscriptionStats {
  active: number;
  trial: number;
  expired: number;
  suspended: number;
  total: number;
  pending_requests: number;
}

const EMPTY_STATS: SubscriptionStats = {
  active: 0,
  trial: 0,
  expired: 0,
  suspended: 0,
  total: 0,
  pending_requests: 0,
};

const toInitials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("") || "E";

const SysAdminSubscriptionManagement: React.FC = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [pageSize, setPageSize] = useState(10);
  const [stats, setStats] = useState<SubscriptionStats>(EMPTY_STATS);
  const [pagination, setPagination] = useState<PaginationInfo>({
    current_page: 1,
    per_page: 10,
    total: 0,
    total_pages: 0,
  });

  const [renewalModalVisible, setRenewalModalVisible] = useState(false);
  const [editExpiryModalVisible, setEditExpiryModalVisible] = useState(false);
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [planModalVisible, setPlanModalVisible] = useState(false);
  const [termsModalVisible, setTermsModalVisible] = useState(false);
  const [restrictionsModalVisible, setRestrictionsModalVisible] = useState(false);
  const [regularizationModalVisible, setRegularizationModalVisible] = useState(false);
  const [requestsModalVisible, setRequestsModalVisible] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);

  const activeRequestRef = useRef<AbortController | null>(null);
  const requestSequenceRef = useRef(0);
  const { hasPermission, refreshUserStatus } = useAuth();
  const canViewSubscriptions = hasPermission("Admin.Souscription.view");
  const canManageSubscriptions = hasPermission("Admin.Souscription.manage");

  const fetchSubscriptions = useCallback(
    async (page = 1, limit = 10, search = "", status = "all") => {
      const requestId = requestSequenceRef.current + 1;
      requestSequenceRef.current = requestId;
      activeRequestRef.current?.abort();

      const controller = new AbortController();
      activeRequestRef.current = controller;
      setLoading(true);

      try {
        const params = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
        });

        if (search) params.set("search", search);
        if (status !== "all") params.set("status", status);

        const response = await fetch(`/api/subscription/companies?${params}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            "Content-Type": "application/json",
          },
          signal: controller.signal,
        });
        const data = await response.json();

        if (!response.ok || !data.success) {
          throw new Error(
            data.message || "Erreur lors du chargement des souscriptions",
          );
        }

        if (requestId !== requestSequenceRef.current) return;

        setCompanies(data.data.companies || []);
        setPagination(data.data.pagination);
        setStats({ ...EMPTY_STATS, ...(data.data.stats || {}) });
      } catch (error) {
        if ((error as Error).name !== "AbortError") {
          message.error(
            error instanceof Error
              ? error.message
              : "Erreur de connexion lors du chargement des souscriptions",
          );
        }
      } finally {
        if (requestId === requestSequenceRef.current) {
          setLoading(false);
        }
      }
    },
    [],
  );

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(searchText.trim());
    }, searchText ? SEARCH_DELAY_MS : 0);

    return () => window.clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    if (canViewSubscriptions) {
      fetchSubscriptions(1, pageSize, debouncedSearch, statusFilter);
    }
  }, [
    canViewSubscriptions,
    debouncedSearch,
    fetchSubscriptions,
    pageSize,
    statusFilter,
  ]);

  useEffect(
    () => () => {
      activeRequestRef.current?.abort();
    },
    [],
  );

  const handlePageChange = useCallback(
    (page: number, nextPageSize?: number) => {
      const resolvedPageSize = nextPageSize || pageSize;

      if (resolvedPageSize !== pageSize) {
        setPageSize(resolvedPageSize);
        return;
      }

      fetchSubscriptions(
        page,
        resolvedPageSize,
        debouncedSearch,
        statusFilter,
      );
    },
    [debouncedSearch, fetchSubscriptions, pageSize, statusFilter],
  );

  const handleRefresh = useCallback(() => {
    fetchSubscriptions(
      pagination.current_page,
      pageSize,
      debouncedSearch,
      statusFilter,
    );
  }, [
    debouncedSearch,
    fetchSubscriptions,
    pageSize,
    pagination.current_page,
    statusFilter,
  ]);

  const closeSelectedModal = useCallback(() => {
    setRenewalModalVisible(false);
    setEditExpiryModalVisible(false);
    setDetailsModalVisible(false);
    setPlanModalVisible(false);
    setTermsModalVisible(false);
    setRestrictionsModalVisible(false);
    setRegularizationModalVisible(false);
    setSelectedCompany(null);
  }, []);

  const openModalFor = useCallback(
    (
      company: Company,
      modal:
        | "details"
        | "plan"
        | "renewal"
        | "expiry"
        | "terms"
        | 'restrictions'
        | "regularization",
    ) => {
      setSelectedCompany(company);
      setDetailsModalVisible(modal === "details");
      setPlanModalVisible(modal === "plan");
      setRenewalModalVisible(modal === "renewal");
      setEditExpiryModalVisible(modal === "expiry");
      setTermsModalVisible(modal === "terms");
      setRestrictionsModalVisible(modal === 'restrictions');
      setRegularizationModalVisible(modal === "regularization");
    },
    [],
  );

  const handleMutationSuccess = useCallback(() => {
    closeSelectedModal();
    handleRefresh();
    refreshUserStatus?.();
  }, [closeSelectedModal, handleRefresh, refreshUserStatus]);

  const updateSubscriptionStatus = useCallback(
    async (
      company: Company,
      status: "active" | "suspended",
      revisionReason: string,
    ) => {
      try {
        const response = await fetch(
          `/api/subscription/companies/${company.id}`,
          {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("authToken")}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ status, revision_reason: revisionReason }),
          },
        );
        const data = await response.json();

        if (!response.ok || !data.success) {
          throw new Error(data.message || "La mise à jour a échoué");
        }

        message.success(
          status === "suspended"
            ? "Souscription suspendue"
            : "Souscription réactivée",
        );
        handleRefresh();
      } catch (error) {
        message.error(
          error instanceof Error ? error.message : "Erreur de connexion",
        );
        throw error;
      }
    },
    [handleRefresh],
  );

  const confirmSubscriptionStatus = useCallback(
    (company: Company, status: "active" | "suspended") => {
      let revisionReason = "";
      Modal.confirm({
        title:
          status === "suspended"
            ? "Suspendre la souscription"
            : "Réactiver la souscription",
        content: (
          <div>
            <p>
              Cette décision s'appliquera immédiatement à {company.name}.
            </p>
            <Input.TextArea
              rows={3}
              maxLength={500}
              placeholder="Motif obligatoire (3 caractères minimum)"
              onChange={(event) => {
                revisionReason = event.target.value;
              }}
            />
          </div>
        ),
        okText: status === "suspended" ? "Suspendre" : "Réactiver",
        okButtonProps: status === "suspended" ? { danger: true } : undefined,
        cancelText: "Annuler",
        onOk: async () => {
          if (revisionReason.trim().length < 3) {
            message.error("Le motif de la décision est obligatoire");
            return Promise.reject();
          }
          await updateSubscriptionStatus(
            company,
            status,
            revisionReason.trim(),
          );
        },
      });
    },
    [updateSubscriptionStatus],
  );

  const getActionItems = useCallback(
    (company: Company): NonNullable<MenuProps["items"]> => {
      const items: NonNullable<MenuProps["items"]> = [
        {
          key: "view",
          icon: <EyeOutlined />,
          label: "Voir les détails",
          onClick: () => openModalFor(company, "details"),
        },
      ];

      if (!canManageSubscriptions) return items;

      items.push(
        {
          key: "commercial-terms",
          icon: <BankOutlined />,
          label: "Conditions commerciales",
          onClick: () => openModalFor(company, "terms"),
        },
        {
          key: 'subscription-restrictions',
          icon: <StopOutlined />,
          label: 'Restrictions de souscription',
          onClick: () => openModalFor(company, 'restrictions'),
        },
        {
          key: "change-plan",
          icon: <SwapOutlined />,
          label: "Changer de plan",
          onClick: () => openModalFor(company, "plan"),
        },
        {
          key: "renew",
          icon: <ClockCircleOutlined />,
          label: "Renouveler",
          onClick: () => openModalFor(company, "renewal"),
        },
        {
          key: "regularize-billing",
          icon: <HistoryOutlined />,
          label: "Régulariser l'historique",
          onClick: () => openModalFor(company, "regularization"),
        },
        {
          key: "edit-expiry",
          icon: <CalendarOutlined />,
          label: "Réviser l'échéance",
          onClick: () => openModalFor(company, "expiry"),
        },
        { type: "divider" },
      );

      if (company.subscription_status === "suspended") {
        items.push({
          key: "reactivate",
          icon: <CheckCircleOutlined />,
          label: "Réactiver",
          onClick: () => confirmSubscriptionStatus(company, "active"),
        });
      } else {
        items.push({
          key: "suspend",
          icon: <StopOutlined />,
          label: "Suspendre",
          danger: true,
          onClick: () => confirmSubscriptionStatus(company, "suspended"),
        });
      }

      return items;
    },
    [canManageSubscriptions, confirmSubscriptionStatus, openModalFor],
  );

  const columns = useMemo(
    () => [
      {
        title: "Entreprise",
        dataIndex: "name",
        key: "name",
        width: 310,
        render: (name: string, record: Company) => (
          <div className="subscription-company-cell">
            <span className="subscription-company-cell__avatar">
              {toInitials(name)}
            </span>
            <div className="subscription-company-cell__copy">
              <span className="subscription-company-cell__name">{name}</span>
              <span className="subscription-company-cell__meta">
                {record.email || record.phone || "Coordonnées non renseignées"}
              </span>
            </div>
          </div>
        ),
      },
      {
        title: "Plan",
        dataIndex: "plan_name",
        key: "plan_name",
        width: 190,
        render: (planName: string | null, record: Company) =>
          planName ? (
            <div className="subscription-plan-cell">
              <span className="subscription-plan-cell__name">{planName}</span>
              <span className="subscription-plan-cell__price">
                {formatSubscriptionNumber(record.active_warehouse_count || 0)} Warehouse(s) actif(s)
              </span>
            </div>
          ) : (
            <Text type="secondary">Aucun plan</Text>
          ),
      },
      {
        title: "Statut",
        dataIndex: "subscription_status",
        key: "subscription_status",
        width: 130,
        render: (status: Company["subscription_status"]) => (
          <SubscriptionStatusBadge status={status} />
        ),
      },
      {
        title: "Utilisateurs",
        dataIndex: "total_users",
        key: "total_users",
        width: 150,
        render: (totalUsers: number, record: Company) => {
          const isOverLimit = Boolean(
            record.plan_max_users && totalUsers > record.plan_max_users,
          );
          const tooltip = isOverLimit
            ? `${formatSubscriptionNumber(totalUsers)} comptes pour une limite de ${formatSubscriptionNumber(record.plan_max_users)}`
            : `${formatSubscriptionNumber(record.active_users)} compte(s) actif(s) sur ${formatSubscriptionNumber(totalUsers)}`;

          return (
            <Tooltip title={tooltip}>
              <div className="subscription-users-cell">
                <Badge
                  dot={record.active_users > 0}
                  color={isOverLimit ? "#cf3c45" : "#22a06b"}
                  offset={[-2, 4]}
                >
                  <span className="subscription-users-cell__icon">
                    <UserOutlined />
                  </span>
                </Badge>
                <div>
                  <span className="subscription-users-cell__total">
                    {formatSubscriptionNumber(totalUsers)}
                  </span>
                  <span
                    className={`subscription-users-cell__label${
                      isOverLimit ? " is-over-limit" : ""
                    }`}
                  >
                    {isOverLimit
                      ? `Limite : ${formatSubscriptionNumber(record.plan_max_users)}`
                      : record.active_users === totalUsers
                        ? "comptes actifs"
                        : `${formatSubscriptionNumber(record.active_users)} actifs`}
                  </span>
                </div>
              </div>
            </Tooltip>
          );
        },
      },
      {
        title: "Expiration",
        key: "expiration",
        width: 185,
        render: (_: unknown, record: Company) => {
          const expiry = record.licence_expire_on || record.trial_ends_at;

          if (!expiry) return <Text type="secondary">Non définie</Text>;

          const isExpired =
            record.subscription_status === "expired" ||
            (record.days_remaining !== null && record.days_remaining < 0);

          return (
            <div className="subscription-expiry-cell">
              <span
                className={
                  isExpired
                    ? "subscription-expiry-cell__date is-expired"
                    : "subscription-expiry-cell__date"
                }
              >
                {formatSubscriptionDate(expiry)}
              </span>
              <span className="subscription-expiry-cell__remaining">
                {record.subscription_status === "trial"
                  ? "Fin de la période d'essai"
                  : record.days_remaining === null
                    ? "Échéance contractuelle"
                    : record.days_remaining >= 0
                      ? `${record.days_remaining} jour(s) restant(s)`
                      : `Expirée depuis ${Math.abs(record.days_remaining)} jour(s)`}
              </span>
            </div>
          );
        },
      },
      {
        title: "",
        key: "actions",
        width: 64,
        fixed: "right" as const,
        render: (_: unknown, record: Company) => (
          <Dropdown
            menu={{ items: getActionItems(record) }}
            trigger={["click"]}
          >
            <Button
              type="text"
              className="subscription-action-button"
              icon={<MoreOutlined />}
              aria-label={`Actions pour ${record.name}`}
            />
          </Dropdown>
        ),
      },
    ],
    [getActionItems],
  );

  const statCards = useMemo(
    () => [
      {
        key: "active",
        label: "Actives",
        value: stats.active,
        icon: <CheckCircleOutlined />,
        tone: "success",
        helper: "Souscriptions en cours",
      },
      {
        key: "trial",
        label: "En essai",
        value: stats.trial,
        icon: <ExperimentOutlined />,
        tone: "trial",
        helper: "Périodes d'essai ouvertes",
      },
      {
        key: "expired",
        label: "Expirées",
        value: stats.expired,
        icon: <WarningOutlined />,
        tone: "danger",
        helper: "Échéances dépassées",
      },
      {
        key: "total",
        label: "Total",
        value: stats.total,
        icon: <AppstoreOutlined />,
        tone: "primary",
        helper: `${formatSubscriptionNumber(stats.suspended)} suspendue(s)`,
      },
    ],
    [stats],
  );

  if (!canViewSubscriptions) {
    return (
      <div className="subscription-access-denied">
        <ShopOutlined />
        <Title level={4}>Accès non autorisé</Title>
        <Text type="secondary">
          Vous n'avez pas les permissions nécessaires pour accéder à cette
          page.
        </Text>
      </div>
    );
  }

  const rangeStart =
    pagination.total === 0
      ? 0
      : (pagination.current_page - 1) * pagination.per_page + 1;
  const rangeEnd = Math.min(
    pagination.current_page * pagination.per_page,
    pagination.total,
  );

  return (
    <div className="subscription-page">
      <section className="subscription-page__hero">
        <div>
          <span className="subscription-page__eyebrow">
            <ShopOutlined /> Administration des abonnements
          </span>
          <Title level={2}>Souscriptions clients</Title>
          <Text>
            Suivez les plans, les échéances et l'utilisation réelle de chaque
            compte entreprise.
          </Text>
        </div>

        <Space wrap className="subscription-page__hero-actions">
          <AdminPortfolioExportButtons kind="subscriptions" />
          <Badge
            count={stats.pending_requests}
            size="small"
            offset={[-4, 3]}
          >
            <Button
              icon={<InboxOutlined />}
              onClick={() => setRequestsModalVisible(true)}
            >
              Demandes
            </Button>
          </Badge>
          {hasPermission("Admin.Souscription.Invoices.view") && (
            <Button
              icon={<FileTextOutlined />}
              onClick={() => {
                window.location.href = "/souscription/factures";
              }}
            >
              Facturation
            </Button>
          )}
          {hasPermission("Admin.Souscription.Plans.view") && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => window.open("/souscription/plans", "_blank")}
            >
              Gérer les plans
            </Button>
          )}
        </Space>
      </section>

      <Row gutter={[16, 16]} className="subscription-stats">
        {statCards.map((stat) => (
          <Col xs={24} sm={12} xl={6} key={stat.key}>
            <Card className={`subscription-stat-card is-${stat.tone}`}>
              <span className="subscription-stat-card__icon">{stat.icon}</span>
              <div className="subscription-stat-card__content">
                <span className="subscription-stat-card__label">
                  {stat.label}
                </span>
                <strong>{formatSubscriptionNumber(stat.value)}</strong>
                <span className="subscription-stat-card__helper">
                  {stat.helper}
                </span>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      <Card className="subscription-workspace" bordered={false}>
        <div className="subscription-toolbar">
          <div className="subscription-toolbar__search">
            <Input
              allowClear
              aria-label="Rechercher une souscription"
              placeholder="Rechercher par entreprise, e-mail, téléphone ou plan..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              suffix={
                searchText.trim() !== debouncedSearch ? (
                  <span className="subscription-toolbar__searching">
                    Recherche…
                  </span>
                ) : null
              }
            />
            <span className="subscription-toolbar__hint">
              Les résultats se mettent à jour automatiquement.
            </span>
          </div>

          <div className="subscription-toolbar__controls">
            <Select
              value={statusFilter}
              onChange={setStatusFilter}
              aria-label="Filtrer par statut"
              options={[
                { value: "all", label: "Tous les statuts" },
                { value: "active", label: "Actives" },
                { value: "trial", label: "En essai" },
                { value: "expired", label: "Expirées" },
                { value: "suspended", label: "Suspendues" },
              ]}
            />
            <Tooltip title="Actualiser les données">
              <Button
                aria-label="Actualiser les souscriptions"
                icon={<ReloadOutlined spin={loading} />}
                onClick={handleRefresh}
                disabled={loading}
              >
                Actualiser
              </Button>
            </Tooltip>
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={companies}
          loading={loading}
          rowKey="id"
          pagination={false}
          scroll={{ x: 1060 }}
          className="subscription-table"
          locale={{
            emptyText: debouncedSearch
              ? "Aucune souscription ne correspond à cette recherche."
              : "Aucune souscription à afficher.",
          }}
        />

        <div className="subscription-pagination">
          <span>
            {rangeStart}–{rangeEnd} sur {formatSubscriptionNumber(pagination.total)}
            {debouncedSearch || statusFilter !== "all"
              ? " résultat(s) filtré(s)"
              : " souscription(s)"}
          </span>
          <Pagination
            current={pagination.current_page}
            total={pagination.total}
            pageSize={pagination.per_page}
            onChange={handlePageChange}
            showSizeChanger
            showQuickJumper
            pageSizeOptions={["10", "20", "50", "100"]}
            showTotal={(total, range) =>
              `${range[0]}–${range[1]} sur ${formatSubscriptionNumber(total)}`
            }
          />
        </div>
      </Card>

      <SubscriptionDetailsModal
        open={detailsModalVisible}
        company={selectedCompany}
        onClose={closeSelectedModal}
      />

      <CompanySubscriptionTermsModal
        open={termsModalVisible}
        company={selectedCompany}
        onClose={closeSelectedModal}
        onSuccess={handleMutationSuccess}
      />

      <CompanySubscriptionRestrictionsModal
        open={restrictionsModalVisible}
        company={selectedCompany}
        onClose={closeSelectedModal}
        onSuccess={handleMutationSuccess}
      />

      <SubscriptionBillingRegularizationModal
        open={regularizationModalVisible}
        company={selectedCompany}
        onClose={closeSelectedModal}
        onSuccess={handleMutationSuccess}
      />

      <PlanUpgradeModal
        visible={planModalVisible}
        company={selectedCompany}
        onSuccess={handleMutationSuccess}
        onCancel={closeSelectedModal}
      />

      <SubscriptionRenewalEnhancedModal
        visible={renewalModalVisible}
        company={selectedCompany}
        onSuccess={handleMutationSuccess}
        onCancel={closeSelectedModal}
      />

      <SubscriptionEditExpiryModal
        visible={editExpiryModalVisible}
        company={selectedCompany}
        onSuccess={handleMutationSuccess}
        onCancel={closeSelectedModal}
      />

      <SubscriptionRequestsModal
        open={requestsModalVisible}
        onClose={() => setRequestsModalVisible(false)}
        onUpdated={handleRefresh}
      />
    </div>
  );
};

const GestionSouscription: React.FC = () => {
  const { user } = useAuth();

  return user?.is_superadmin ? (
    <SysAdminSubscriptionManagement />
  ) : (
    <SubscriberSubscriptionPortal />
  );
};

export default GestionSouscription;
